import { getBaseUrl } from './baseUrl';

export type SupportTicketSurface = 'TECH_DASHBOARD' | 'CUSTOMER_PORTAL';
export type SupportTicketCategory = 'SOFTWARE' | 'ACCESS' | 'LICENSING' | 'DATA' | 'OTHER';
export type SupportTicketPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
export type SupportTicketAttachment = { id: string; messageId: string | null; fileName: string; mimeType: string | null; sizeBytes: number; createdAt: string; downloadPath: string; previewPath: string };
export type MySupportTicket = {
  id: string; ticketNumber: string; applicationSurface: string; pagePath: string | null; pageLabel: string | null;
  category: string; priority: string; status: string; subject: string; description: string; createdAt: string; updatedAt: string;
  attachments: SupportTicketAttachment[];
  messages: Array<{ id: string; authorType: string; authorName: string | null; body: string; createdAt: string; attachments: SupportTicketAttachment[] }>;
};

type DiagnosticError = { at: string; source: string; message: string };
const recentErrors: DiagnosticError[] = [];
let diagnosticsInstalled = false;

function rememberError(source: string, message: unknown) {
  const normalized = String(message || 'Unknown client error').slice(0, 1000);
  recentErrors.push({ at: new Date().toISOString(), source: source.slice(0, 80), message: normalized });
  if (recentErrors.length > 20) recentErrors.splice(0, recentErrors.length - 20);
}

export function installSupportDiagnostics() {
  if (diagnosticsInstalled || typeof window === 'undefined') return;
  diagnosticsInstalled = true;
  window.addEventListener('error', event => rememberError('window.error', event.message));
  window.addEventListener('unhandledrejection', event => rememberError('unhandledrejection', event.reason instanceof Error ? event.reason.message : event.reason));
  window.addEventListener('bonzaiq:api-error', event => rememberError('api', (event as CustomEvent<{ message?: string }>).detail?.message));
}

export function buildSupportDiagnosticSnapshot() {
  installSupportDiagnostics();
  return {
    clientTimestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    language: navigator.language,
    online: navigator.onLine,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    recentErrors: [...recentErrors]
  };
}

async function parseResponse(response: Response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || 'Support request failed');
  return payload;
}

export async function createSupportTicket(input: {
  applicationSurface: SupportTicketSurface;
  pagePath: string;
  pageLabel: string;
  category: SupportTicketCategory;
  priority: SupportTicketPriority;
  subject: string;
  description: string;
  files?: File[];
}) {
  const base = await getBaseUrl();
  const form = new FormData();
  Object.entries(input).forEach(([key, value]) => { if (key !== 'files' && value !== undefined) form.append(key, String(value)); });
  form.append('diagnosticSnapshot', JSON.stringify(buildSupportDiagnosticSnapshot()));
  (input.files || []).forEach(file => form.append('files', file));
  const response = await fetch(`${base}/api/support-tickets`, { method: 'POST', body: form });
  return parseResponse(response) as Promise<{ ticket: { id: string; ticketNumber: string; status: string; createdAt: string } }>;
}

export async function fetchMySupportTickets() {
  const base = await getBaseUrl();
  return parseResponse(await fetch(`${base}/api/support-tickets`, { cache: 'no-store' })) as Promise<{ tickets: MySupportTicket[] }>;
}

export async function fetchMySupportTicket(ticketId: string) {
  const base = await getBaseUrl();
  return parseResponse(await fetch(`${base}/api/support-tickets/${encodeURIComponent(ticketId)}`, { cache: 'no-store' })) as Promise<{ ticket: MySupportTicket }>;
}

export async function replyToMySupportTicket(ticketId: string, body: string, files: File[] = []) {
  const base = await getBaseUrl();
  const form = new FormData();
  form.append('body', body);
  files.forEach(file => form.append('files', file));
  return parseResponse(await fetch(`${base}/api/support-tickets/${encodeURIComponent(ticketId)}/messages`, { method: 'POST', body: form }));
}

export async function downloadMySupportAttachment(attachment: SupportTicketAttachment) {
  const base = await getBaseUrl();
  const response = await fetch(`${base}${attachment.downloadPath}`, { cache: 'no-store' });
  if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || 'Attachment download failed');
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement('a');
  anchor.href = url; anchor.download = attachment.fileName; anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function fetchMySupportAttachmentFile(attachment: SupportTicketAttachment) {
  const base = await getBaseUrl();
  const response = await fetch(`${base}${attachment.downloadPath}`, { cache: 'no-store' });
  if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || 'Attachment could not be loaded');
  const blob = await response.blob();
  return { blob, contentType: response.headers.get('Content-Type') || blob.type || attachment.mimeType || 'application/octet-stream' };
}

export async function fetchMySupportAttachmentPreview(attachment: SupportTicketAttachment) {
  const base = await getBaseUrl();
  return parseResponse(await fetch(`${base}${attachment.previewPath}`, { cache: 'no-store' })) as Promise<{ kind: 'TEXT' | 'DOCUMENT_TEXT' | 'BINARY'; content: string | null }>;
}
